import { useId } from 'react';
import { Plus, X } from 'lucide-react';
import type { PaigeFieldMapping } from './paige.global';
import { useJobFieldNames } from './use-job-field-names';

export interface FieldMapEditorProps {
  map: PaigeFieldMapping[];
  onChange: (map: PaigeFieldMapping[]) => void;
  /** What the product calls a field, for the target column's heading. */
  targetLabel: string;
  /** The box's title and its one-line explanation, when they differ from a field map's. */
  legend?: string;
  hint?: string;
  /** The field names to choose from (default: the job's); typed names when there are none. */
  fields?: readonly string[];
  /** What an empty target means, as its placeholder ("Same name", "Leave out"). */
  emptyTarget?: string;
  /** The product's field names, offered as suggestions for the target (a test lists them). */
  targets?: readonly string[];
}

/**
 * A destination's field map (Content Central, Nucleus One, a storage destination's metadata): one
 * line per Paige field, chosen from the job's fields, and the product's name for it. What a field not
 * listed and an empty target mean is the product's, said in `hint` and `emptyTarget`.
 */
export function FieldMapEditor(props: FieldMapEditorProps) {
  const { map, onChange, targetLabel, legend, hint, emptyTarget = 'Leave out', targets } = props;
  const jobFields = useJobFieldNames();
  const fields = props.fields ?? jobFields;
  const update = (index: number, patch: Partial<PaigeFieldMapping>) =>
    onChange(map.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  const remove = (index: number) => onChange(map.filter((_, i) => i !== index));
  const listId = useId();
  const targetsId = targets && targets.length > 0 ? listId : undefined;
  return (
    <fieldset className="paige-field-map">
      <legend>{legend ?? 'Field map'}</legend>
      <small>{hint ?? defaultHint}</small>
      {targetsId && <TargetList id={targetsId} targets={targets ?? []} />}
      {map.length > 0 && <MapHead targetLabel={targetLabel} />}
      {map.map((line, index) => (
        <FieldMapLine
          key={index}
          line={line}
          targetLabel={targetLabel}
          fields={fields}
          emptyTarget={emptyTarget}
          targetsId={targetsId}
          onChange={(patch) => update(index, patch)}
          onRemove={() => remove(index)}
        />
      ))}
      <button
        type="button"
        className="quiet-button small"
        onClick={() => onChange([...map, { field: '', target: '' }])}
      >
        <Plus size={12} aria-hidden="true" />
        Map a field
      </button>
    </fieldset>
  );
}

/** The product's field names a test listed, offered on every line's target. */
function TargetList({ id, targets }: { id: string; targets: readonly string[] }) {
  return (
    <datalist id={id}>
      {targets.map((target) => (
        <option key={target} value={target} />
      ))}
    </datalist>
  );
}

const defaultHint =
  'Fields not listed go under their own name. Leave the target blank to skip a field.';

function MapHead({ targetLabel }: { targetLabel: string }) {
  return (
    <span className="paige-field-map-head">
      <span>Paige field</span>
      <span>{targetLabel}</span>
    </span>
  );
}

interface LineProps {
  line: PaigeFieldMapping;
  targetLabel: string;
  fields: readonly string[];
  emptyTarget: string;
  targetsId?: string;
  onChange: (patch: Partial<PaigeFieldMapping>) => void;
  onRemove: () => void;
}

function FieldMapLine(props: LineProps) {
  const { line, targetLabel, fields, emptyTarget, targetsId, onChange, onRemove } = props;
  return (
    <span className="paige-field-map-line">
      <FieldChoice value={line.field} fields={fields} onChange={(field) => onChange({ field })} />
      <input
        value={line.target}
        onChange={(e) => onChange({ target: e.target.value })}
        placeholder={emptyTarget}
        aria-label={targetLabel}
        list={targetsId}
        autoComplete="off"
      />
      <button
        type="button"
        className="quiet-button small"
        onClick={onRemove}
        aria-label="Remove line"
      >
        <X size={12} aria-hidden="true" />
      </button>
    </span>
  );
}

/**
 * The Paige field of one line: a choice of the job's fields, keeping a name saved before that the
 * job no longer lists; a typed name when the job's fields are not known.
 */
function FieldChoice(props: {
  value: string;
  fields: readonly string[];
  onChange: (field: string) => void;
}) {
  const { value, fields, onChange } = props;
  if (fields.length === 0)
    return (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Invoice number"
        aria-label="Paige field"
        required
      />
    );
  const known = value === '' || fields.some((f) => f.toLowerCase() === value.toLowerCase());
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Paige field"
      required
    >
      <option value="" disabled>
        Choose a field
      </option>
      {!known && <option value={value}>{value}</option>}
      {fields.map((field) => (
        <option key={field} value={field}>
          {field}
        </option>
      ))}
    </select>
  );
}
